import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Icon,
  Pressable,
  Progress,
  ProgressFilledTrack,
} from '@gluestack-ui/themed';
import {
  Gauge,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  Clock,
} from 'lucide-react-native';
import { StudyPlan, PACE_CONFIG, StudyPace } from '@/student/types/StudyPlan';

interface StudyPacingProps {
  studyPlan: StudyPlan | null;
  updatePace: (pace: StudyPace) => void;
  projectedGraduationDate: Date | null;
  progressPercentage: number;
  modulesCompleted: number;
  totalModules: number;
}

export const StudyPacing: React.FC<StudyPacingProps> = ({
  studyPlan,
  updatePace,
  projectedGraduationDate,
  progressPercentage,
  modulesCompleted,
  totalModules,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!studyPlan) return null;

  const currentConfig = PACE_CONFIG[studyPlan.pace];

  return (
    <Box
      mt="$4"
      bg="$bgCard"
      borderRadius="$lg"
      borderWidth={1}
      borderColor="$borderLight"
      overflow="hidden"
    >
      <Pressable onPress={() => setIsExpanded(!isExpanded)}>
        {({ pressed }) => (
          <Box
            p="$4"
            bg={pressed ? '$bgHover' : 'transparent'}
          >
            <VStack space="md">
              <HStack justifyContent="space-between" alignItems="center">
                <HStack space="md" alignItems="center">
                  <Box bg="$bgSubtle" p="$2" borderRadius="$full">
                    <Icon as={Gauge} size="sm" color="$brandBase" />
                  </Box>
                  <VStack>
                    <Heading size="xs" fontWeight="900">
                      Study Velocity:
                      {' '}
                      {currentConfig.label}
                    </Heading>
                  </VStack>
                </HStack>

                <HStack space="sm" alignItems="center">
                  <VStack alignItems="flex-end">
                    <Text size="2xs" color="$textSubtle" textTransform="uppercase" fontWeight="bold">
                      Est. Graduation
                    </Text>
                    <Text size="xs" fontWeight="bold">
                      {projectedGraduationDate?.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
                    </Text>
                  </VStack>
                  <Icon as={isExpanded ? ChevronUp : ChevronDown} size="sm" color="$textSubtle" />
                </HStack>
              </HStack>

              <VStack space="xs">
                <HStack justifyContent="space-between" alignItems="center">
                  <Text size="2xs" color="$textSubtle" fontWeight="bold">
                    PROGRAM PROGRESS
                  </Text>
                  <Text size="2xs" color="$textSubtle" fontWeight="bold">
                    {modulesCompleted}
                    /
                    {totalModules}
                    {' '}
                    MODULES
                  </Text>
                </HStack>
                <Progress value={progressPercentage} size="xs" w="$full" bg="$bgSubtle">
                  <ProgressFilledTrack bg="$brandBase" />
                </Progress>
              </VStack>

              <HStack space="md" flexWrap="wrap">
                <HStack space="xs" alignItems="center" bg="$bgSubtle" px="$2" py="$1" borderRadius="$md">
                  <Icon as={Clock} size="2xs" color="$textSubtle" />
                  <Text size="2xs" color="$textSubtle" fontWeight="bold">
                    {currentConfig.weeksPerModule}
                    {' '}
                    WEEKS / MODULE
                  </Text>
                </HStack>
                <HStack space="xs" alignItems="center" bg="$bgSubtle" px="$2" py="$1" borderRadius="$md">
                  <Icon as={CalendarClock} size="2xs" color="$textSubtle" />
                  <Text size="2xs" color="$textSubtle" fontWeight="bold">
                    {currentConfig.hours}
                    H / WEEK
                  </Text>
                </HStack>
              </HStack>
            </VStack>
          </Box>
        )}
      </Pressable>

      {isExpanded && (
        <Box
          p="$6"
          borderTopWidth={1}
          borderColor="$borderLight"
          bg="$bgSubtle"
        >
          <VStack space="xl">
            <VStack space="xs">
              <Heading size="sm" fontWeight="900">Adjust Your Pace</Heading>
              <Text size="xs" color="$textSubtle">
                How many hours can you commit per week? This will update your projected graduation date and module targets.
              </Text>
            </VStack>

            <HStack space="md" w="100%">
              {(Object.keys(PACE_CONFIG) as StudyPace[]).map((paceKey) => {
                const config = PACE_CONFIG[paceKey];
                const isSelected = studyPlan.pace === paceKey;

                return (
                  <Pressable
                    key={paceKey}
                    flex={1}
                    onPress={() => updatePace(paceKey)}
                  >
                    {({ pressed }) => (
                      <Box
                        p="$3"
                        borderRadius="$md"
                        borderWidth={2}
                        borderColor={isSelected ? '$brandBase' : '$borderLight'}
                        bg={isSelected ? '$brandBase' : '$bgCard'}
                        opacity={pressed ? 0.9 : 1}
                        alignItems="center"
                        sx={{
                          ':hover': {
                            borderColor: isSelected ? '$brandBase' : '$borderDark',
                          },
                        }}
                      >
                        <Text
                          size="sm"
                          fontWeight="900"
                          color={isSelected ? '$textOnBrand' : '$textHeading'}
                          textAlign="center"
                        >
                          {config.label}
                        </Text>
                        <Text
                          size="xs"
                          fontWeight="bold"
                          color={isSelected ? '$textOnBrandMuted' : '$textSubtle'}
                          mt="$1"
                        >
                          {`${config.hours}h`}
                        </Text>
                      </Box>
                    )}
                  </Pressable>
                );
              })}
            </HStack>

            <Text size="xs" color="$textSubtle" textAlign="center" italic>
              {`At ${currentConfig.hours} hours/week, you will aim to finish a module every ${currentConfig.weeksPerModule} weeks.`}
            </Text>
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export default StudyPacing;
